import type { PersonContact } from "@/shared/types/estateContact";

interface SignoffLoopInput {
  contacts: PersonContact[];
  adminMode: string;
  userEmail: string | null;
  topPersonId?: string;
  coApplicantAnswerRaw: string | string[] | undefined;
}

interface SignoffLoopResult {
  loopContacts: (PersonContact & { id: number })[];
  selectedAdminId: number | null;
  selectedAdminTier: number | null;
  coApplicantIds: number[];
}

export function computeSignoffLoop(input: SignoffLoopInput): SignoffLoopResult {
  const { contacts, adminMode, userEmail, topPersonId, coApplicantAnswerRaw } = input;
  const isSomeoneElse = adminMode === "someone_else";

  const selectedAdminId: number | null = (() => {
    if (topPersonId) {
      const id = Number(topPersonId);
      if (!isNaN(id)) return id;
    }
    if (isSomeoneElse || !userEmail) return null;
    const meContact = contacts.find(
      (c) => c.email && c.email.toLowerCase() === userEmail.toLowerCase(),
    );
    return meContact?.id ?? null;
  })();

  const selectedAdminTier: number | null = (() => {
    if (selectedAdminId !== null) {
      const admin = contacts.find((c) => c.id === selectedAdminId);
      if (admin?.priority_tier != null) return admin.priority_tier;
    }
    const distributees = contacts.filter((c) => c.is_distributee && c.can_serve);
    if (distributees.length === 0) return null;
    return Math.min(...distributees.map((c) => c.priority_tier ?? 99));
  })();

  const coApplicantIds: number[] = (() => {
    if (adminMode !== "co") return [];
    if (typeof coApplicantAnswerRaw !== "string" || !coApplicantAnswerRaw) return [];
    try {
      return JSON.parse(coApplicantAnswerRaw) as number[];
    } catch {
      return [];
    }
  })();

  const loopContacts = selectedAdminTier === null
    ? []
    : contacts.filter((c): c is PersonContact & { id: number } => {
        if (!c.is_distributee) return false;
        if (!c.can_serve) return false;
        if (c.priority_tier !== selectedAdminTier) return false;
        if (selectedAdminId !== null && c.id === selectedAdminId) return false;
        if (coApplicantIds.includes(c.id)) return false;
        if (c.person_status === "passed_away") return false;
        if (c.additional_details?.includes("minor")) return false;
        return true;
      });

  return { loopContacts, selectedAdminId, selectedAdminTier, coApplicantIds };
}
